import React, { memo, useCallback, useEffect, useState } from "react";
import { Handle, Position, useReactFlow } from "@xyflow/react";
import { SelectionOverlay } from "./SelectionOverlay";

const StarNodeContent = ({
  data,
  selected,
  id,
  width,
  height,
  ...props
}) => {
  const { getNode } = useReactFlow();
  const [nodePosition, setNodePosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const updatePosition = () => {
      const node = getNode(id);
      if (node) {
        setNodePosition(node.position);
      }
    };
    updatePosition();
    const interval = setInterval(updatePosition, 100);
    return () => clearInterval(interval);
  }, [id, getNode]);

  const nodeData = data;
  const nodeWidth = width || nodeData.width || 180;
  const nodeHeight = height || nodeData.height || 180;
  const rotation = nodeData.rotation || 0;
  const backgroundColor =
    nodeData.backgroundColor || "rgba(156, 163, 175, 0.4)";
  const borderColor = nodeData.borderColor || "#374151";
  const borderWidth = nodeData.borderWidth || 1;

  const handleResize = useCallback(
    (newWidth, newHeight, newPosition) => {
      const resizeEvent = new CustomEvent("nodeResize", {
        detail: {
          nodeId: id,
          nodeType: "star",
          width: Math.round(newWidth),
          height: Math.round(newHeight),
          position: newPosition,
        },
      });
      window.dispatchEvent(resizeEvent);
    },
    [id],
  );

  const handleRotate = useCallback(
    (newRotation) => {
      const rotateEvent = new CustomEvent("nodeRotate", {
        detail: {
          nodeId: id,
          nodeType: "star",
          rotation: newRotation,
        },
      });
      window.dispatchEvent(rotateEvent);
    },
    [id],
  );

  const isLocked = nodeData.locked || false;
  const borderStyle = nodeData.borderStyle || "solid";
  const lineCap = nodeData.lineCap || "butt";
  const lineJoin = nodeData.lineJoin || "miter";
  const shadow = nodeData.shadowSettings || {
    offsetX: 0,
    offsetY: 0,
    blur: 0,
    color: "#000000",
  };

  const numPoints = nodeData.starPoints || 5;
  const innerRadiusPerc = nodeData.starInnerRadius || 40;
  const outerRadiusPerc = nodeData.starOuterRadius || 100;

  const getDashArray = (style) => {
    switch (style) {
      case "dashed":
        return "10,5";
      case "dotted":
        return "2,3";
      default:
        return "none";
    }
  };

  const generateStarPoints = (points, inner, outer) => {
    const results = [];
    const centerX = 90;
    const centerY = 90;
    const outerR = (90 * outer) / 100 - borderWidth / 2;
    const innerR = (90 * inner) / 100 - borderWidth / 2;
    const step = Math.PI / points;

    for (let i = 0; i < 2 * points; i++) {
      const r = i % 2 === 0 ? outerR : innerR;
      const angle = i * step - Math.PI / 2;
      const x = centerX + r * Math.cos(angle);
      const y = centerY + r * Math.sin(angle);
      results.push(`${x},${y}`);
    }
    return results.join(" ");
  };

  return (
    <div className="relative w-full h-full" data-id={id}>
      {!isLocked && selected && (
        <SelectionOverlay
          selected={selected || false}
          onRotate={handleRotate}
          onResize={handleResize}
          minWidth={50}
          minHeight={30}
          nodeId={id}
          width={nodeWidth}
          height={nodeHeight}
          rotation={rotation}
          nodeX={nodePosition.x}
          nodeY={nodePosition.y}
        />
      )}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 180 180"
        preserveAspectRatio="none"
        style={{
          transform: `rotate(${rotation}deg) scale(${nodeData.flippedH ? -1 : 1}, ${nodeData.flippedV ? -1 : 1})`,
          transformOrigin: "center",
          filter: shadow.blur > 0 || shadow.offsetX !== 0 || shadow.offsetY !== 0 
            ? `drop-shadow(${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blur}px ${shadow.color})` 
            : "none",
        }}
      >
        <polygon
          points={generateStarPoints(numPoints, innerRadiusPerc, outerRadiusPerc)}
          style={{
            fill: backgroundColor,
            stroke: borderColor,
            strokeWidth: borderWidth,
            strokeDasharray: getDashArray(borderStyle),
            strokeLinecap: lineCap,
            strokeLinejoin: lineJoin,
          }}
        />
      </svg>

      <Handle
        type="target"
        position={Position.Top}
        className="w-2 h-2 bg-blue-500 border-2 border-white opacity-0 hover:opacity-100"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-2 h-2 bg-blue-500 border-2 border-white opacity-0 hover:opacity-100"
      />
      <Handle
        type="target"
        position={Position.Left}
        className="w-2 h-2 bg-blue-500 border-2 border-white opacity-0 hover:opacity-100"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="w-2 h-2 bg-blue-500 border-2 border-white opacity-0 hover:opacity-100"
      />
    </div>
  );
};

export const StarNode = memo(StarNodeContent);
StarNode.displayName = "StarNode";
